import { CardColorSchema, type CompanyCardTheme } from '../model/card/companyCard'
import {
  BLACK_HEX,
  WHITE_HEX,
  ensureContrast,
  meetsContrast,
  mixColors,
  mostReadableColor,
  type ColorWithOpacity,
} from './color'

export const CARD_PAGE_COLOR = '#FBF7F0'
export const CARD_DEFAULT_BANNER_COLOR = '#14385E'
export const CARD_DEFAULT_ACCENT_COLOR = '#F5A623'
export const CARD_BANNER_PRESETS: string[] = [CARD_DEFAULT_BANNER_COLOR, '#3B4654', '#285A90', '#61544E']
export const CARD_ACCENT_PRESETS: string[] = [CARD_DEFAULT_ACCENT_COLOR, '#F8BA00', '#64A5DF', '#10B981']

const CARD_NAMED_COLORS = new Map<string, string>([
  ['navy', '#14385E'],
  ['slate', '#3B4654'],
  ['blue', '#3877B4'],
  ['tan', '#C0801A'],
])

const CARD_INK = '#111827'
const GRADIENT_SHADE = '#001226'
const TEXT_MIN_CONTRAST = 4.5
const ICON_MIN_CONTRAST = 3
const DERIVED_INK_CONTRAST = 7
const MUTED_TEXT_OPACITY = 0.8
const TILE_OPACITY = 0.1
const TILE_HOVER_OPACITY = 0.2

const GRADIENT_SHADE_AMOUNTS: number[] = Array.from({ length: 29 }, (_, index) => (28 - index) / 100)

export type CardThemeTokens = {
  banner: string
  bannerEnd: string
  onBanner: string
  onBannerMuted: string
  bannerTile: ColorWithOpacity
  bannerTileHover: ColorWithOpacity
  accent: string
  onAccent: string
  accentOnBanner: string
  accentIconOnBanner: string
  accentOnLight: string
  accentTextOnLight: string
  ink: string
  page: string
}

type BannerGradient = {
  banner: string
  bannerEnd: string
}

type BannerTiles = {
  bannerTile: ColorWithOpacity
  bannerTileHover: ColorWithOpacity
}

type TileContrastInput = {
  gradient: BannerGradient
  onBanner: string
  tile: ColorWithOpacity
}

type GradientContrastTarget = BannerGradient & {
  color: string
  minRatio: number
}

function toCardHex(color: string | undefined, fallback: string): string {
  const parsed = CardColorSchema.safeParse(color)
  if (!parsed.success) return fallback
  return CARD_NAMED_COLORS.get(parsed.data) ?? parsed.data.toUpperCase()
}

function readableTextOn(background: string): string {
  const preferred = mostReadableColor({ background, candidates: [WHITE_HEX, CARD_INK] })
  if (meetsContrast({ color: preferred, background, minRatio: TEXT_MIN_CONTRAST })) return preferred
  return mostReadableColor({ background, candidates: [WHITE_HEX, BLACK_HEX] })
}

function meetsContrastOnGradient({ color, banner, bannerEnd, minRatio }: GradientContrastTarget): boolean {
  if (!meetsContrast({ color, background: banner, minRatio })) return false
  return meetsContrast({ color, background: bannerEnd, minRatio })
}

function toBannerEnd(banner: string, onBanner: string): string {
  const amount = GRADIENT_SHADE_AMOUNTS.find((step) =>
    meetsContrast({
      color: onBanner,
      background: mixColors({ from: banner, to: GRADIENT_SHADE, amount: step }),
      minRatio: TEXT_MIN_CONTRAST,
    })
  )
  return mixColors({ from: banner, to: GRADIENT_SHADE, amount: amount ?? 0 })
}

function tileKeepsTextReadable({ gradient, onBanner, tile }: TileContrastInput): boolean {
  const tileOn = (stop: string): string => mixColors({ from: stop, to: tile.color, amount: tile.opacity })
  return meetsContrastOnGradient({
    color: onBanner,
    banner: tileOn(gradient.banner),
    bannerEnd: tileOn(gradient.bannerEnd),
    minRatio: TEXT_MIN_CONTRAST,
  })
}

function toBannerTiles(gradient: BannerGradient, onBanner: string): BannerTiles {
  const glassHover: ColorWithOpacity = { color: onBanner, opacity: TILE_HOVER_OPACITY }
  const tint = tileKeepsTextReadable({ gradient, onBanner, tile: glassHover })
    ? onBanner
    : mostReadableColor({ background: onBanner, candidates: [WHITE_HEX, BLACK_HEX] })
  return {
    bannerTile: { color: tint, opacity: TILE_OPACITY },
    bannerTileHover: { color: tint, opacity: TILE_HOVER_OPACITY },
  }
}

function toOnBannerMuted(gradient: BannerGradient, onBanner: string): string {
  const muted = mixColors({ from: gradient.banner, to: onBanner, amount: MUTED_TEXT_OPACITY })
  if (meetsContrastOnGradient({ ...gradient, color: muted, minRatio: TEXT_MIN_CONTRAST })) return muted
  return onBanner
}

function toInk(banner: string): string {
  if (meetsContrast({ color: banner, background: CARD_PAGE_COLOR, minRatio: TEXT_MIN_CONTRAST })) return banner
  return ensureContrast({ color: banner, background: CARD_PAGE_COLOR, minRatio: DERIVED_INK_CONTRAST })
}

function toOnAccent(accent: string, ink: string): string {
  if (meetsContrast({ color: ink, background: accent, minRatio: TEXT_MIN_CONTRAST })) return ink
  return readableTextOn(accent)
}

export function resolveCardTheme(theme: CompanyCardTheme | undefined): CardThemeTokens {
  const banner = toCardHex(theme?.bannerColor, CARD_DEFAULT_BANNER_COLOR)
  const accent = toCardHex(theme?.accentColor, CARD_DEFAULT_ACCENT_COLOR)
  const onBanner = readableTextOn(banner)
  const gradient: BannerGradient = { banner, bannerEnd: toBannerEnd(banner, onBanner) }
  const ink = toInk(banner)
  const accentReadsOnBanner = meetsContrastOnGradient({ ...gradient, color: accent, minRatio: TEXT_MIN_CONTRAST })
  const accentIconReadsOnBanner = meetsContrastOnGradient({ ...gradient, color: accent, minRatio: ICON_MIN_CONTRAST })
  return {
    ...gradient,
    onBanner,
    onBannerMuted: toOnBannerMuted(gradient, onBanner),
    ...toBannerTiles(gradient, onBanner),
    accent,
    onAccent: toOnAccent(accent, ink),
    accentOnBanner: accentReadsOnBanner ? accent : onBanner,
    accentIconOnBanner: accentIconReadsOnBanner ? accent : onBanner,
    accentOnLight: ensureContrast({ color: accent, background: CARD_PAGE_COLOR, minRatio: ICON_MIN_CONTRAST }),
    accentTextOnLight: ensureContrast({ color: accent, background: CARD_PAGE_COLOR, minRatio: TEXT_MIN_CONTRAST }),
    ink,
    page: CARD_PAGE_COLOR,
  }
}
